import React, { useState, useCallback, useEffect } from 'react';
import { useAtom } from 'jotai';
import {
  DndContext,
  DragEndEvent,
  DragStartEvent,
  DragOverEvent,
  DragOverlay,
  useSensor,
  useSensors,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  pointerWithin
} from '@dnd-kit/core';
import { AddIcon } from '../ios/IOSIcons';
import { motion, AnimatePresence } from 'framer-motion';
import {
  newTaskProjectIdAtom,
  isTaskFabDraggingAtom,
  isTaskItemDraggingAtom,
  draggedTaskIdAtom
} from '../../../../atoms/v3/taskAtoms';
import { projectsAtom } from '../../../../atoms/v0/projectAtoms';
import { DroppableAreaType, ImportanceCategory, TimeGroupCategory } from './DroppableArea';
import { Task } from '../../../../types/task';
import { TaskItem } from './TaskItem';
import { trackDragEvents, trackQuickActionEvents } from '../../../../lib/analyticsEnhanced';
import { isTouchDevice } from '../../../../utils/platformUtils';


// 创建任务参数接口
export interface CreateTaskParams {
  projectId?: string;
  importance?: ImportanceCategory;
  timeGroup?: TimeGroupCategory;
}

// 更新任务参数接口
export interface UpdateTaskParams {
  projectId?: string;
  importance?: ImportanceCategory;
  timeGroup?: TimeGroupCategory;
}

interface TaskDndContextProps {
  children: React.ReactNode;
  onCreateTask: (params: CreateTaskParams) => void;
  onUpdateTask?: (taskId: string, params: UpdateTaskParams) => void; // 新增：更新任务的回调函数
}

/**
 * 任务拖放上下文组件
 * 提供拖放功能和视觉反馈
 */
export const TaskDndContext: React.FC<TaskDndContextProps> = ({
  children,
  onCreateTask,
  onUpdateTask
}) => {
  // 活动区域状态
  const [activeAreaType, setActiveAreaType] = useState<DroppableAreaType | null>(null);
  const [activeProjectId, setActiveProjectId] = useState<string | null>(null);
  const [activeProjectName, setActiveProjectName] = useState<string | null>(null);
  const [activeImportance, setActiveImportance] = useState<ImportanceCategory | null>(null);
  const [activeTimeGroup, setActiveTimeGroup] = useState<TimeGroupCategory | null>(null);

  // 提示文本状态
  const [hintText, setHintText] = useState<string>('拖放到区域快速创建任务');

  // 任务项拖拽状态
  const [draggedTask, setDraggedTask] = useState<Task | null>(null);

  // Atoms
  const [projects] = useAtom(projectsAtom);
  const [, setNewTaskProjectId] = useAtom(newTaskProjectIdAtom);
  const [isFabDragging, setIsFabDragging] = useAtom(isTaskFabDraggingAtom);
  const [isTaskItemDragging, setIsTaskItemDragging] = useAtom(isTaskItemDraggingAtom);
  const [draggedTaskId, setDraggedTaskId] = useAtom(draggedTaskIdAtom);

  // 检测是否为触摸设备 - 使用统一的平台检测函数
  const isTouch = isTouchDevice();

  // 触觉反馈函数
  const triggerHapticFeedback = useCallback(async () => {
    if (isTouch) {
      try {
        // 优先使用 Capacitor Haptics API
        const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
        const { Capacitor } = await import('@capacitor/core');

        if (Capacitor.isNativePlatform()) {
          await Haptics.impact({ style: ImpactStyle.Light });
        } else {
          // 降级到浏览器 vibrate API
          if ('vibrate' in navigator) {
            navigator.vibrate(50); // 50ms 轻微震动
          }
        }
      } catch (error) {
        // 如果 Haptics 导入失败，降级到浏览器 API
        if ('vibrate' in navigator) {
          navigator.vibrate(50);
        }
      }
    }
  }, [isTouch]);

  // 重置拖拽状态函数
  const resetDragState = useCallback(() => {
    setIsFabDragging(false);
    setIsTaskItemDragging(false);
    setDraggedTaskId(null);
    setDraggedTask(null);
    setActiveAreaType(null);
    setActiveProjectId(null);
    setActiveProjectName(null);
    setActiveImportance(null);
    setActiveTimeGroup(null);
    setHintText('拖放到区域快速创建任务');
  }, [setIsFabDragging, setIsTaskItemDragging, setDraggedTaskId]);

  // 添加错误恢复机制 - 如果拖拽状态卡住，自动重置
  useEffect(() => {
    let timeoutId: NodeJS.Timeout;

    if (isFabDragging || isTaskItemDragging) {
      // 如果拖拽状态持续超过 10 秒，自动重置（防止卡死）
      timeoutId = setTimeout(() => {
        console.warn('[TaskDndContext] Drag state timeout, auto-resetting...');
        resetDragState();
      }, 10000);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [isFabDragging, isTaskItemDragging, resetDragState]);

  // 配置传感器 - 根据设备类型使用不同的传感器
  const sensors = useSensors(
    // 根据设备类型选择合适的传感器
    ...(isTouch ? [
      // 触摸设备：只使用 TouchSensor，PointerSensor 在移动端有问题
      useSensor(TouchSensor, {
        activationConstraint: {
          delay: 500, // 500ms 长按延迟，避免与滚动冲突
          tolerance: 10, // 10px 移动容差
        },
      }),
      // 添加键盘支持（可选）
      useSensor(KeyboardSensor),
    ] : [
      // 桌面端：使用 MouseSensor，快速响应
      useSensor(MouseSensor, {
        activationConstraint: {
          distance: 5, // 5px 距离激活
        },
      }),
      // 桌面端键盘支持
      useSensor(KeyboardSensor),
    ])
  );

  // 处理拖拽开始
  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;

    // 触发触觉反馈（移动端长按激活时）
    triggerHapticFeedback();

    if (active.id === 'task-fab') {
      // 处理FAB拖拽开始
      setIsFabDragging(true);

      // 记录FAB拖拽开始事件
      trackDragEvents.dragStarted('fab', 'task-fab', {
        is_touch: isTouch,
        drag_source: 'floating_action_button'
      });

      // 重置所有活动区域状态
      setActiveAreaType(null);
      setActiveProjectId(null);
      setActiveProjectName(null);
      setActiveImportance(null);
      setActiveTimeGroup(null);

      // 设置默认提示文本
      setHintText(isTouch ? '长按拖拽到区域创建任务' : '拖放到区域快速创建任务');
    } else if (typeof active.id === 'string' && active.id.startsWith('task-')) {
      // 处理任务项拖拽开始
      setIsTaskItemDragging(true);
      const taskId = active.id.replace('task-', '');
      setDraggedTaskId(taskId);
      setDraggedTask(active.data.current?.task || null);

      // 记录任务项拖拽开始事件
      trackDragEvents.dragStarted('task_item', taskId, {
        is_touch: isTouch,
        drag_source: 'task_item',
        task_completed: active.data.current?.task?.done || false,
        has_project: !!active.data.current?.task?.projectId
      });

      // 设置默认提示文本
      setHintText(isTouch ? '长按拖拽到区域移动任务' : '拖放到区域移动任务');

      // 重置所有活动区域状态
      setActiveAreaType(null);
      setActiveProjectId(null);
      setActiveProjectName(null);
      setActiveImportance(null);
      setActiveTimeGroup(null);
    }
  };

  // 处理拖拽悬停
  const handleDragOver = (event: DragOverEvent) => {
    const { active, over } = event;

    if (active.id === 'task-fab') {
      // 处理FAB拖拽悬停
      // 如果没有悬停在任何区域上，重置所有状态
      if (!over || !over.data.current) {
        if (activeAreaType !== null) {
          setActiveAreaType(null);
          setActiveProjectId(null);
          setActiveProjectName(null);
          setActiveImportance(null);
          setActiveTimeGroup(null);
          setHintText('拖放到区域快速创建任务');
        }
        return;
      }

      // 获取当前悬停区域的类型和数据
      const areaData = over.data.current;
      const areaType = areaData.type;

      // 根据区域类型更新状态
      if (areaType === 'project') {
        // 项目区域
        const currentProjectId = areaData.projectId;

        if (activeAreaType !== 'project' || currentProjectId !== activeProjectId) {
          setActiveAreaType('project');
          setActiveProjectId(currentProjectId);
          setActiveImportance(null);
          setActiveTimeGroup(null);

          // 查找项目名称
          const project = projects.find(p => p.id === currentProjectId);
          const projectName = project ? project.title : '未命名项目';
          setActiveProjectName(projectName);

          // 更新提示文本
          setHintText(`拖放到此创建属于"${projectName}"的新任务`);
        }
      } else if (areaType === 'importance') {
        // 重要性区域
        const importance = areaData.importance;

        if (activeAreaType !== 'importance' ||
            activeImportance?.vital !== importance.vital ||
            activeImportance?.urgent !== importance.urgent) {
          setActiveAreaType('importance');
          setActiveProjectId(null);
          setActiveProjectName(null);
          setActiveImportance(importance);
          setActiveTimeGroup(null);

          // 更新提示文本
          setHintText(`拖放到此创建${importance.label}的新任务`);
        }
      } else if (areaType === 'time') {
        // 时间区域
        const timeGroup = areaData.timeGroup;

        if (activeAreaType !== 'time' || activeTimeGroup !== timeGroup) {
          setActiveAreaType('time');
          setActiveProjectId(null);
          setActiveProjectName(null);
          setActiveImportance(null);
          setActiveTimeGroup(timeGroup);

          // 更新提示文本
          let timeLabel = '';
          switch (timeGroup) {
            case 'today':
              timeLabel = '今天';
              break;
            case 'tomorrow':
              timeLabel = '明天';
              break;
            case 'yesterday':
              timeLabel = '昨天';
              break;
          }

          setHintText(`拖放到此创建${timeLabel}的新任务`);
        }
      }
    } else if (typeof active.id === 'string' && active.id.startsWith('task-') && over) {
      // 处理任务项拖拽悬停
      // 如果没有悬停在任何区域上，重置所有状态
      if (!over.data.current) {
        if (activeAreaType !== null) {
          setActiveAreaType(null);
          setActiveProjectId(null);
          setActiveProjectName(null);
          setActiveImportance(null);
          setActiveTimeGroup(null);
          setHintText('拖放到区域移动任务');
        }
        return;
      }

      // 获取当前悬停区域的类型和数据
      const areaData = over.data.current;
      const areaType = areaData.type;

      // 根据区域类型更新状态
      if (areaType === 'project') {
        // 项目区域
        const currentProjectId = areaData.projectId;

        if (activeAreaType !== 'project' || currentProjectId !== activeProjectId) {
          setActiveAreaType('project');
          setActiveProjectId(currentProjectId);
          setActiveImportance(null);
          setActiveTimeGroup(null);

          // 查找项目名称
          const project = projects.find(p => p.id === currentProjectId);
          const projectName = project ? project.title : '未命名项目';
          setActiveProjectName(projectName);

          // 更新提示文本
          setHintText(`移动任务到"${projectName}"`);
        }
      } else if (areaType === 'importance') {
        // 重要性区域
        const importance = areaData.importance;

        if (activeAreaType !== 'importance' ||
            activeImportance?.vital !== importance.vital ||
            activeImportance?.urgent !== importance.urgent) {
          setActiveAreaType('importance');
          setActiveProjectId(null);
          setActiveProjectName(null);
          setActiveImportance(importance);
          setActiveTimeGroup(null);

          // 更新提示文本
          setHintText(`将任务设为${importance.label}`);
        }
      } else if (areaType === 'time') {
        // 时间区域
        const timeGroup = areaData.timeGroup;

        if (activeAreaType !== 'time' || activeTimeGroup !== timeGroup) {
          setActiveAreaType('time');
          setActiveProjectId(null);
          setActiveProjectName(null);
          setActiveImportance(null);
          setActiveTimeGroup(timeGroup);

          // 更新提示文本
          let timeLabel = '';
          switch (timeGroup) {
            case 'today':
              timeLabel = '今天';
              break;
            case 'tomorrow':
              timeLabel = '明天';
              break;
            case 'yesterday':
              timeLabel = '昨天';
              break;
          }

          setHintText(`将任务移动到${timeLabel}`);
        }
      }
    }
  };

  // 处理拖拽结束
  const handleDragEnd = (event: DragEndEvent) => {
    console.log('handleDragEnd triggered. Event:', event);
    const { active, over, delta } = event;

    if (active.id === 'task-fab') {
      // 处理FAB拖拽结束
      // 使用delta计算拖拽距离 - 这是dnd-kit提供的准确拖拽位移信息
      const dragDistance = Math.sqrt(Math.pow(delta.x, 2) + Math.pow(delta.y, 2));

      // 定义拖拽阈值 - 超过这个距离才算是有效拖拽
      const dragThreshold = 50; // 50px

      // 获取视口高度和取消区域高度
      const viewportHeight = window.innerHeight;
      const cancelZoneHeight = 64; // 取消区域高度

      // 获取当前鼠标/触摸位置的Y坐标
      const clientY = (event.activatorEvent as PointerEvent)?.clientY ??
                     (event.activatorEvent as TouchEvent)?.changedTouches?.[0]?.clientY ?? 0;

      console.log('  Drop target (over):', over);
      console.log(`  Delta: x=${delta.x}, y=${delta.y}`);
      console.log(`  Drag distance: ${dragDistance}px, Threshold: ${dragThreshold}px`);
      console.log(`  Client Y: ${clientY}, Viewport H: ${viewportHeight}, Cancel Zone: > ${viewportHeight - cancelZoneHeight}`);
      console.log(`  Active area type: ${activeAreaType}`);

      if (over) {
        console.log('  Over data:', over.data.current);
      }

      // 判断是否在取消区域释放
      let droppedInCancelZone = false;
      if (clientY > viewportHeight - cancelZoneHeight) {
        console.log('  Condition met: Dropped in cancel zone.');
        droppedInCancelZone = true;
      }

      // 创建任务参数对象
      const taskParams: CreateTaskParams = {};

      // 判断拖拽行为
      if (dragDistance < dragThreshold) {
        // 如果拖拽距离小于阈值，视为点击，触发默认新建任务
        console.log('  Condition met: Small drag distance, treating as click.');
        onCreateTask(taskParams); // 传递空对象表示默认新建
      } else if (!droppedInCancelZone && over && over.data.current) {
        // 如果拖拽距离大于阈值，且拖放到有效区域，根据区域类型创建任务
        const areaData = over.data.current;
        const areaType = areaData.type;

        if (areaType === 'project' && areaData.projectId) {
          // 项目区域
          const projectId = areaData.projectId;
          console.log(`  Condition met: Dropped on project area (${projectId}) after significant drag.`);

          // 设置项目ID
          taskParams.projectId = projectId;

          // 更新atom状态（为了兼容性）
          setNewTaskProjectId(projectId);

        } else if (areaType === 'importance' && areaData.importance) {
          // 重要性区域
          const importance = areaData.importance;
          console.log(`  Condition met: Dropped on importance area (vital=${importance.vital}, urgent=${importance.urgent}) after significant drag.`);

          // 设置重要性
          taskParams.importance = importance;

        } else if (areaType === 'time' && areaData.timeGroup) {
          // 时间区域
          const timeGroup = areaData.timeGroup;
          console.log(`  Condition met: Dropped on time area (${timeGroup}) after significant drag.`);

          // 设置时间组
          taskParams.timeGroup = timeGroup;
        }

        // 创建任务
        onCreateTask(taskParams);
      } else {
        console.log('  Condition not met: Drop cancelled or target invalid/in cancel zone.');
        // 如果拖拽距离大于阈值，但没有拖放到有效区域，不触发任何操作
      }
    } else if (typeof active.id === 'string' && active.id.startsWith('task-')) {
      // 处理任务项拖拽结束
      const taskId = active.id.replace('task-', '');

      // 如果拖放到有效区域，更新任务
      if (over && over.data.current && onUpdateTask) {
        const areaData = over.data.current;
        const areaType = areaData.type;
        const updateParams: UpdateTaskParams = {};

        if (areaType === 'project' && areaData.projectId) {
          // 项目区域
          updateParams.projectId = areaData.projectId;
          console.log(`  Updating task ${taskId} to project ${areaData.projectId}`);
        } else if (areaType === 'importance' && areaData.importance) {
          // 重要性区域
          updateParams.importance = areaData.importance;
          console.log(`  Updating task ${taskId} to importance vital=${areaData.importance.vital}, urgent=${areaData.importance.urgent}`);
        } else if (areaType === 'time' && areaData.timeGroup) {
          // 时间区域
          updateParams.timeGroup = areaData.timeGroup;
          console.log(`  Updating task ${taskId} to time group ${areaData.timeGroup}`);
        }

        // 更新任务
        if (Object.keys(updateParams).length > 0) {
          onUpdateTask(taskId, updateParams);
        }
      }
    }

    // 重置所有状态
    resetDragState();
  };

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
      collisionDetection={pointerWithin}
    >
      {children}

      {/* 拖拽覆盖层 - 显示正在拖拽的元素 */}
      <DragOverlay>
        {isFabDragging && (
          <div className="w-14 h-14 rounded-full bg-ios-blue dark:bg-ios-dark-blue text-white flex items-center justify-center shadow-2xl">
            <div className="w-7 h-7">
              <AddIcon />
            </div>
          </div>
        )}
        {isTaskItemDragging && draggedTask && (
          <div className="bg-white dark:bg-ios-dark-gray5 rounded-ios-lg shadow-ios dark:shadow-ios-dark max-w-md">
            <TaskItem
              task={draggedTask}
              onToggle={() => {}}
              onClick={() => {}}
              isFirst={true}
              isLast={true}
            />
          </div>
        )}
      </DragOverlay>

      {/* 动态提示文本覆盖层 */}
      <AnimatePresence>
        {(isFabDragging || isTaskItemDragging) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 pointer-events-none z-40 flex items-center justify-center"
          >
            <div className="bg-black/50 backdrop-blur-sm text-white px-4 py-2 rounded-full text-sm font-medium">
              {hintText}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </DndContext>
  );
};
